import React, { useEffect, useMemo, useState } from "react";
import { product, Order, User } from "../types";
import { Category } from "../interfaces/product";
import Sidebar from "../components/Sidebar";
import "./Dashboard.css";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler,
} from "chart.js";

import { Bar, Doughnut, Line } from "react-chartjs-2";
import Loading from "../components/Loading";

import {
  fetchUsersAndOrders,
  fetchProducts,
  fetchCounts,
} from "../graphql/api/userApi";

import { fetchCategoriesAndSubcategories } from "../graphql/api/categoryService";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler
);

const Dashboard: React.FC = () => {
  const [products, setProducts] = useState<product[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const [topProduct, setTopProduct] = useState<product | null>(null);

  const [counts, setCounts] = useState({
    vendorCount: 0,
    shopCount: 0,
    categoryCount: 0,
    productCount: 0,
  });

  const [dateRange, setDateRange] = useState("Last 30 Days");

  // ---------------------------------------------------------
  // LOAD DATA
  // ---------------------------------------------------------

  const loadData = async () => {
    setLoading(true);

    try {
      const [
        productsData,
        { users: usersData, orders: ordersData },
        { categories: categoriesArray },
      ] = await Promise.all([
        fetchProducts(),
        fetchUsersAndOrders(),
        fetchCategoriesAndSubcategories(),
      ]);

      setProducts(productsData || []);
      setUsers(usersData || []);
      setOrders(ordersData || []);
      setCategories(categoriesArray || []);
    } catch (error) {
      console.error("Failed to load dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getCounts = async () => {
    try {
      const data = await fetchCounts();

      setCounts({
        vendorCount: data.vendorCount || 0,
        shopCount: data.shopCount || 0,
        categoryCount: data.categoryCount || 0,
        productCount: data.productCount || 0,
      });
    } catch (error) {
      console.error("Failed to load counts:", error);
    }
  };

  useEffect(() => {
    loadData();
    getCounts();
  }, []);

  // ---------------------------------------------------------
  // LOOKUP MAPS
  // ---------------------------------------------------------

  const userIdToNameMap = useMemo(() => {
    const map: { [key: string]: string } = {};

    users.forEach((user) => {
      map[user.id] = user.username;
    });

    return map;
  }, [users]);

  const productIdToDetailsMap = useMemo(() => {
    const map: { [key: string]: product } = {};

    products.forEach((item) => {
      map[item.id] = item;
    });

    return map;
  }, [products]);

  const categoryIdToNameMap = useMemo(() => {
    const map: { [key: string]: string } = {};

    categories.forEach((category) => {
      map[category.id] = category.name;
    });

    return map;
  }, [categories]);

  // ---------------------------------------------------------
  // ORDER STATUS
  // ---------------------------------------------------------

  const pendingOrders = orders.filter(
    (order) => order.status?.toLowerCase() === "pending"
  );

  const shippedOrders = orders.filter(
    (order) => order.status?.toLowerCase() === "shipped"
  );

  const deliveredOrders = orders.filter(
    (order) => order.status?.toLowerCase() === "delivered"
  );

  const cancelledOrders = orders.filter(
    (order) => order.status?.toLowerCase() === "cancelled"
  );

  // ---------------------------------------------------------
  // REVENUE
  // ---------------------------------------------------------

  const totalRevenue = orders.reduce(
    (total, order) => total + Number(order.totalAmount || 0),
    0
  );

  const averageOrderValue =
    orders.length > 0 ? totalRevenue / orders.length : 0;

  // ---------------------------------------------------------
  // PRODUCT SALES
  // ---------------------------------------------------------

  const productSales = useMemo(() => {
    const sales: { [key: string]: number } = {};

    orders.forEach((order) => {
      order.items?.forEach((item) => {
        sales[item.productId] =
          (sales[item.productId] || 0) + Number(item.quantity || 0);
      });
    });

    return sales;
  }, [orders]);

  useEffect(() => {
    if (products.length === 0) {
      setTopProduct(null);
      return;
    }

    let maxSales = 0;
    let top: product | null = null;

    products.forEach((item) => {
      const sales = productSales[item.id] || 0;

      if (sales > maxSales) {
        maxSales = sales;
        top = item;
      }
    });

    setTopProduct(top);
  }, [products, productSales]);

  // ---------------------------------------------------------
  // CATEGORY SALES
  // ---------------------------------------------------------

  const categoryCounts = useMemo(() => {
    const result: { [key: string]: number } = {};

    orders.forEach((order) => {
      order.items?.forEach((item) => {
        const productData = productIdToDetailsMap[item.productId];

        if (productData) {
          const categoryName =
            categoryIdToNameMap[productData.categoryId] || "Uncategorized";

          result[categoryName] =
            (result[categoryName] || 0) + Number(item.quantity || 0);
        }
      });
    });

    return result;
  }, [orders, productIdToDetailsMap, categoryIdToNameMap]);

  // ---------------------------------------------------------
  // DAILY REVENUE
  // ---------------------------------------------------------

  const groupedRevenue = useMemo(() => {
    const result: { [date: string]: number } = {};

    orders.forEach((order) => {
      const date = new Date(order.createdAt).toLocaleDateString("en-GB");

      result[date] =
        (result[date] || 0) + Number(order.totalAmount || 0);
    });

    return result;
  }, [orders]);

  // ---------------------------------------------------------
  // CHARTS
  // ---------------------------------------------------------

  const revenueChartData = {
    labels: Object.keys(groupedRevenue),
    datasets: [
      {
        label: "Revenue",
        data: Object.values(groupedRevenue),
        backgroundColor: "rgba(37, 99, 235, 0.15)",
        borderColor: "#2563EB",
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        pointRadius: 3,
      },
    ],
  };

  const revenueChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: "#E5E7EB",
        },
        ticks: {
          callback: (value: string | number) => `₹${value}`,
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
  };

  const categoryChartData = {
    labels: Object.keys(categoryCounts),
    datasets: [
      {
        data: Object.values(categoryCounts),
        backgroundColor: [
          "#2563EB",
          "#10B981",
          "#F59E0B",
          "#EF4444",
          "#8B5CF6",
          "#06B6D4",
          "#EC4899",
          "#F97316",
        ],
        borderWidth: 0,
      },
    ],
  };

  const categoryChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "68%",
    plugins: {
      legend: {
        position: "bottom" as const,
        labels: {
          usePointStyle: true,
          padding: 15,
        },
      },
    },
  };

  const orderStatusData = {
    labels: ["Pending", "Shipped", "Delivered", "Cancelled"],
    datasets: [
      {
        data: [
          pendingOrders.length,
          shippedOrders.length,
          deliveredOrders.length,
          cancelledOrders.length,
        ],
        backgroundColor: [
          "#F59E0B",
          "#3B82F6",
          "#10B981",
          "#EF4444",
        ],
        borderWidth: 0,
      },
    ],
  };

  // ---------------------------------------------------------
  // RECENT ORDERS
  // ---------------------------------------------------------

  const recentOrders = [...orders]
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() -
        new Date(a.createdAt).getTime()
    )
    .slice(0, 6);

  // ---------------------------------------------------------
  // RECENT USERS
  // ---------------------------------------------------------

  const recentUsers = [...users].slice(-6).reverse();

  // ---------------------------------------------------------
  // TOP PRODUCTS
  // ---------------------------------------------------------

  const topProducts = [...products]
    .sort(
      (a, b) =>
        (productSales[b.id] || 0) - (productSales[a.id] || 0)
    )
    .slice(0, 5);

  // ---------------------------------------------------------
  // RETURN
  // ---------------------------------------------------------

  return (
    <div className="admin-dashboard">

      <Sidebar
        isOpen={isSidebarOpen}
        setIsOpen={setIsSidebarOpen}
      />

      <main
        className={`dashboard-main ${
          isSidebarOpen ? "sidebar-expanded" : "sidebar-collapsed"
        }`}
      >

        {/* =====================================================
            TOP HEADER
        ===================================================== */}

        {/* <header className="dashboard-topbar">

          <div className="search-box">
            <span>⌕</span>
            <input
              type="text"
              placeholder="Search products, users, orders..."
            />
          </div>

          <div className="topbar-right">

            <button className="notification-btn">
              🔔
              <span className="notification-dot"></span>
            </button>

            <div className="admin-profile">
              <div className="admin-avatar">
                AD
              </div>

              <div>
                <strong>Admin</strong>
                <span>Administrator</span>
              </div>

              <span className="profile-arrow">⌄</span>
            </div>

          </div>

        </header> */}

        {/* =====================================================
            PAGE HEADER
        ===================================================== */}

        <section className="page-header">

          <div>
            <h1>Business Dashboard</h1>

            <p>
              Overview of your marketplace, orders, customers and
              reward business.
            </p>
          </div>

          <div className="header-actions">

            {/* <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
            >
              <option>Today</option>
              <option>Last 7 Days</option>
              <option>Last 30 Days</option>
              <option>Last 90 Days</option>
              <option>This Year</option>
            </select> */}

            <button
              className="refresh-button"
              onClick={() => {
                loadData();
                getCounts();
              }}
            >
              ↻ Refresh
            </button>

          </div>

        </section>

        {loading ? (

          <div className="dashboard-loading">
            <Loading />
          </div>

        ) : (

          <>

            {/* =================================================
                BUSINESS KPI
            ================================================= */}

            <section className="section">

              {/* <div className="section-title-row">
                <div>
                  <h2>Business Overview</h2>
                  <p>Current marketplace performance</p>
                </div>
              </div> */}

              <div className="kpi-grid">

                <div className="kpi-card blue">
                  <div className="kpi-icon">👥</div>
                  <div>
                    <span>Total Users</span>
                    <strong>{users.length}</strong>
                    <small>Registered users</small>
                  </div>
                </div>

                <div className="kpi-card green">
                  <div className="kpi-icon">🏪</div>
                  <div>
                    <span>Total Shops</span>
                    <strong>{counts.shopCount}</strong>
                    <small>Registered shops</small>
                  </div>
                </div>

                <div className="kpi-card orange">
                  <div className="kpi-icon">🏢</div>
                  <div>
                    <span>Total Vendors</span>
                    <strong>{counts.vendorCount}</strong>
                    <small>Business partners</small>
                  </div>
                </div>

                <div className="kpi-card purple">
                  <div className="kpi-icon">📦</div>
                  <div>
                    <span>Total Products</span>
                    <strong>{products.length}</strong>
                    <small>Products listed</small>
                  </div>
                </div>

                <div className="kpi-card cyan">
                  <div className="kpi-icon">🛒</div>
                  <div>
                    <span>Total Orders</span>
                    <strong>{orders.length}</strong>
                    <small>All orders</small>
                  </div>
                </div>

                <div className="kpi-card emerald">
                  <div className="kpi-icon">₹</div>
                  <div>
                    <span>Total Revenue</span>
                    <strong>
                      ₹{totalRevenue.toLocaleString("en-IN", {
                        maximumFractionDigits: 0,
                      })}
                    </strong>
                    <small>Average ₹{averageOrderValue.toFixed(0)} / order</small>
                  </div>
                </div>

              </div>

            </section>

            {/* =================================================
                REVENUE + CATEGORY
            ================================================= */}

            <section className="main-charts-grid">

              <div className="dashboard-card revenue-card">

                <div className="card-header">

                  <div>
                    <h3>Revenue Overview</h3>
                    <p>Business revenue by order date</p>
                  </div>

                  <span className="chart-badge">
                    {dateRange}
                  </span>

                </div>

                <div className="large-chart">
                  {Object.keys(groupedRevenue).length > 0 ? (
                    <Line
                      data={revenueChartData}
                      options={revenueChartOptions}
                    />
                  ) : (
                    <div className="empty-chart">
                      No revenue data available
                    </div>
                  )}
                </div>

              </div>

              <div className="dashboard-card">

                <div className="card-header">

                  <div>
                    <h3>Sales by Category</h3>
                    <p>Products sold by category</p>
                  </div>

                </div>

                <div className="medium-chart">
                  {Object.keys(categoryCounts).length > 0 ? (
                    <Doughnut
                      data={categoryChartData}
                      options={categoryChartOptions}
                    />
                  ) : (
                    <div className="empty-chart">
                      No category sales
                    </div>
                  )}
                </div>

              </div>

            </section>

            {/* =================================================
                ORDER BUSINESS STATUS
            ================================================= */}

            <section className="section">

              <div className="section-title-row">

                <div>
                  <h2>Order Business</h2>
                  <p>Understand where your orders are currently moving</p>
                </div>

              </div>

              <div className="order-summary-grid">

                <div className="order-status-card pending">
                  <span>Pending</span>
                  <strong>{pendingOrders.length}</strong>
                  <small>Need attention</small>
                </div>

                <div className="order-status-card shipped">
                  <span>Shipped</span>
                  <strong>{shippedOrders.length}</strong>
                  <small>On the way</small>
                </div>

                <div className="order-status-card delivered">
                  <span>Delivered</span>
                  <strong>{deliveredOrders.length}</strong>
                  <small>Completed</small>
                </div>

                <div className="order-status-card cancelled">
                  <span>Cancelled</span>
                  <strong>{cancelledOrders.length}</strong>
                  <small>Cancelled orders</small>
                </div>

              </div>

            </section>

            {/* =================================================
                LNG BUSINESS FLOW
            ================================================= */}

            <section className="dashboard-card lng-business-card">

              <div className="card-header">

                <div>
                  <h3>LNG Reward Business Flow</h3>
                  <p>
                    Admin view of how customer purchases move through
                    the reward system
                  </p>
                </div>

                <span className="system-badge">
                  LNG SYSTEM
                </span>

              </div>

              <div className="lng-flow">

                <div className="flow-step customer-step">
                  <div className="flow-icon">👤</div>
                  <strong>Customer</strong>
                  <span>Purchase</span>
                </div>

                <div className="flow-arrow">→</div>

                <div className="flow-step">
                  <div className="flow-icon">🛒</div>
                  <strong>Order</strong>
                  <span>Online / Offline</span>
                </div>

                <div className="flow-arrow">→</div>

                <div className="flow-split">

                  <div className="flow-branch online">
                    <strong>ONLINE</strong>
                    <span>RewardFlag = TRUE</span>
                    <small>28-step calculation</small>
                  </div>

                  <div className="flow-branch offline">
                    <strong>OFFLINE</strong>
                    <span>Shop percentage</span>
                    <small>ID Fund accumulation</small>
                  </div>

                </div>

                <div className="flow-arrow">→</div>

                <div className="flow-step loyalty-step">
                  <div className="flow-icon">⭐</div>
                  <strong>Loyalty ID</strong>
                  <span>₹300 threshold</span>
                </div>

                <div className="flow-arrow">→</div>

                <div className="flow-step reward-step">
                  <div className="flow-icon">🎁</div>
                  <strong>28 Steps</strong>
                  <span>Reward distribution</span>
                </div>

              </div>

              <div className="lng-metrics">

                <div>
                  <span>ID Fund</span>
                  <strong>—</strong>
                  <small>Connect reward API</small>
                </div>

                <div>
                  <span>Loyalty IDs</span>
                  <strong>—</strong>
                  <small>Connect reward API</small>
                </div>

                <div>
                  <span>Active Queue</span>
                  <strong>—</strong>
                  <small>Connect reward API</small>
                </div>

                <div>
                  <span>Rewards Distributed</span>
                  <strong>—</strong>
                  <small>Connect reward API</small>
                </div>

                <div>
                  <span>LNG Pool</span>
                  <strong>—</strong>
                  <small>Connect reward API</small>
                </div>

              </div>

            </section>

            {/* =================================================
                PRODUCTS + ORDERS + USERS
            ================================================= */}

            <section className="three-column-grid">

              {/* TOP PRODUCTS */}

              <div className="dashboard-card">

                <div className="card-header">

                  <div>
                    <h3>Top Products</h3>
                    <p>Best selling products</p>
                  </div>

                  <span className="view-link">
                    View All
                  </span>

                </div>

                <div className="top-products-list">

                  {topProducts.length === 0 ? (

                    <div className="empty-state">
                      No product sales yet
                    </div>

                  ) : (

                    topProducts.map((item, index) => (

                      <div
                        className="top-product-row"
                        key={item.id}
                      >

                        <div className="rank">
                          {index + 1}
                        </div>

                        <div className="product-placeholder">
                          📦
                        </div>

                        <div className="product-row-info">

                          <strong>{item.name}</strong>

                          <span>
                            {productSales[item.id] || 0} units sold
                          </span>

                        </div>

                        <strong className="product-price">
                          ₹{Number(item.price || 0).toLocaleString("en-IN")}
                        </strong>

                      </div>

                    ))

                  )}

                </div>

              </div>

              {/* RECENT ORDERS */}

              <div className="dashboard-card">

                <div className="card-header">

                  <div>
                    <h3>Recent Orders</h3>
                    <p>Latest customer transactions</p>
                  </div>

                  <span className="view-link">
                    View All
                  </span>

                </div>

                <div className="recent-list">

                  {recentOrders.length === 0 ? (

                    <div className="empty-state">
                      No orders yet
                    </div>

                  ) : (

                    recentOrders.map((order) => {

                      const productName =
                        order.items?.length > 0
                          ? productIdToDetailsMap[
                              order.items[0].productId
                            ]?.name || "Product"
                          : "No products";

                      return (

                        <div
                          className="recent-order-row"
                          key={String(order.id)}
                        >

                          <div className="order-avatar">
                            {String(order.id).slice(0, 2).toUpperCase()}
                          </div>

                          <div className="recent-order-info">

                            <strong>
                              #{String(order.id).slice(0, 8)}
                            </strong>

                            <span>
                              {userIdToNameMap[order.userId] ||
                                "Unknown User"}
                            </span>

                            <small>{productName}</small>

                          </div>

                          <div className="recent-order-right">

                            <strong>
                              ₹{Number(order.totalAmount || 0).toLocaleString(
                                "en-IN"
                              )}
                            </strong>

                            <span
                              className={`status-badge ${order.status
                                ?.toLowerCase()
                                .replace(/\s+/g, "-")}`}
                            >
                              {order.status}
                            </span>

                          </div>

                        </div>

                      );

                    })

                  )}

                </div>

              </div>

              {/* RECENT USERS */}

              <div className="dashboard-card">

                <div className="card-header">

                  <div>
                    <h3>Recent Users</h3>
                    <p>Recently registered users</p>
                  </div>

                  <span className="view-link">
                    View All
                  </span>

                </div>

                <div className="recent-list">

                  {recentUsers.length === 0 ? (

                    <div className="empty-state">
                      No users yet
                    </div>

                  ) : (

                    recentUsers.map((user) => (

                      <div
                        className="recent-user-row"
                        key={user.id}
                      >

                        <div className="user-avatar">
                          {user.username
                            ?.substring(0, 2)
                            .toUpperCase()}
                        </div>

                        <div className="recent-user-info">

                          <strong>{user.username}</strong>

                          <span>
                            {user.role}
                          </span>

                        </div>

                        <span
                          className={`role-badge ${user.role?.toLowerCase()}`}
                        >
                          {user.role}
                        </span>

                      </div>

                    ))

                  )}

                </div>

              </div>

            </section>

            {/* =================================================
                ORDER STATUS + BUSINESS SUMMARY
            ================================================= */}

            <section className="bottom-grid">

              <div className="dashboard-card">

                <div className="card-header">

                  <div>
                    <h3>Order Status Distribution</h3>
                    <p>Current order lifecycle</p>
                  </div>

                </div>

                <div className="status-chart">

                  {orders.length > 0 ? (
                    <Doughnut
                      data={orderStatusData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        cutout: "70%",
                        plugins: {
                          legend: {
                            position: "bottom",
                          },
                        },
                      }}
                    />
                  ) : (
                    <div className="empty-chart">
                      No order data
                    </div>
                  )}

                </div>

              </div>

              <div className="dashboard-card business-summary-card">

                <div className="card-header">

                  <div>
                    <h3>Business Summary</h3>
                    <p>Important numbers for the admin</p>
                  </div>

                </div>

                <div className="summary-list">

                  <div>
                    <span>Customers / Users</span>
                    <strong>{users.length}</strong>
                  </div>

                  <div>
                    <span>Shops</span>
                    <strong>{counts.shopCount}</strong>
                  </div>

                  <div>
                    <span>Vendors</span>
                    <strong>{counts.vendorCount}</strong>
                  </div>

                  <div>
                    <span>Categories</span>
                    <strong>{counts.categoryCount}</strong>
                  </div>

                  <div>
                    <span>Products</span>
                    <strong>{products.length}</strong>
                  </div>

                  <div>
                    <span>Orders</span>
                    <strong>{orders.length}</strong>
                  </div>

                  <div>
                    <span>Pending Orders</span>
                    <strong className="warning-text">
                      {pendingOrders.length}
                    </strong>
                  </div>

                  <div>
                    <span>Delivered Orders</span>
                    <strong className="success-text">
                      {deliveredOrders.length}
                    </strong>
                  </div>

                </div>

              </div>

            </section>

          </>

        )}

      </main>

    </div>
  );
};

export default Dashboard;